<template>
  <RatioSpacedContainer
    v-if="isPackageEmpty"
    class="h-full"
  >
    <Empty
      :text="_t('package.empty')"
      :desc="isOwner ? _t('package.create_tip4') : ''"
    ></Empty>
  </RatioSpacedContainer>

  <RatioSpacedContainer
    v-else-if="isChapterEmpty"
    class="h-full"
  >
    <Empty
      :text="_t('package.no_card')"
      :desc="isOwner ? _t('package.create_tip4') : ''"
    ></Empty>
  </RatioSpacedContainer>

  <div class="px-4 flex flex-col relative h-full">
    <div
      v-if="showTitle"
      class="my-2 text-13px text-[var(--text-color-secondary)]"
    >
      {{ _t('common.chapter') }}
    </div>

    <div class="grid-chapter">
      <div
        v-for="chapter in chapters"
        :key="chapter.id"
        class="p-4 rounded-lg space-y-1 bg-slate-100 chapter"
        @click="onChapterClick(chapter)"
      >
        <div class="text-17px line-clamp-1">{{ chapter.title }}</div>
        <div class="text-15px text-gray">
          {{
            _t('package.card_count2', { count: allChapterCountMap[chapter.id] })
          }}
        </div>
      </div>
    </div>

    <div
      v-if="showTitle"
      class="my-2 text-13px text-[var(--text-color-secondary)]"
    >
      {{ _t('common.card') }}
    </div>

    <DynamicScroller
      ref="dynamicScroller"
      :items="showedCards"
      :min-item-size="100"
      key-field="renderId"
      class="py-4 g-safe-area flex-1 h-0"
      @scroll="onScroll"
    >
      <template #before>
        <PackageCoverBanner
          ref="packageCoverBannerRef"
          :package="props.package"
          class="w-full"
        />
        <!-- 虚拟列表里面 padding 以及元素之间的 margin 没有效果，所以用一个固定高度的 div 来模拟 padding -->
        <div class="h-12px"></div>
      </template>
      <template #default="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[item.content]"
          :emit-resize="item.id === currentAnimatingCardId"
          :data-index="index"
          @resize="onResize"
        >
          <slot
            name="card"
            :cardRes="item"
            :noteIndex="index"
          >
            <!-- 虚拟列表里面 padding 以及元素之间的 margin 没有效果，所以用一个固定高度的 div 来模拟 padding -->
            <div
              v-if="index > 0"
              class="h-12px"
            ></div>
            <CardBrowserPad
              :key="item.id"
              :cardResponse="item"
              class="overflow-hidden"
              @click="onCardClick(item)"
            />
          </slot>
        </DynamicScrollerItem>
      </template>
      <template #after>
        <template v-if="isGenerating">
          <!-- 虚拟列表里面 padding 以及元素之间的 margin 没有效果，所以用一个固定高度的 div 来模拟 padding -->
          <div
            v-if="showedCards.length > 0"
            class="h-12px"
          ></div>
          <CardBrowserPad
            :cardResponse="currentAnimatingCardDraft"
            :enable-typewriter="true"
            class="overflow-hidden"
            @typewriter-complete="onTypewriterComplete"
          />
        </template>
        <div
          v-if="isOwner"
          class="flex flex-col justify-center items-center mt-3 w-full gap-3"
        >
          <BottomButtons
            ref="inlineBottomButtons"
            :ai-button-disabled="isAiGenerateDisabled"
            @create-card="onCreateCard"
            @ai-generate="onAICardGenerate"
          />

          <div
            v-if="!_global.isPcMode"
            class="text-15px text-ld-label-secondary text-center leading-[1.4] font-600"
          >
            {{ _t('package.edit_tip1') }}
            <span class="text-ld-brand-500">
              {{ _global.pcLink }}
            </span>

            <br />
            {{ _t('package.edit_tip2') }}
          </div>

          <!-- 虚拟列表里面 padding 以及元素之间的 margin 没有效果，所以用一个固定高度的 div 来模拟 padding -->
          <div
            v-if="bottomPadding > 0"
            :style="{
              paddingBottom: bottomPadding + 'px',
            }"
          ></div>
          <div :class="[showFixedBottomButtons ? 'h-20' : 'h-4']"></div>
        </div>
      </template>
    </DynamicScroller>

    <div
      v-if="showFixedBottomButtons && isOwner"
      class="absolute bottom-0 left-0 w-full bg-ld-background"
    >
      <BottomButtons
        :ai-button-disabled="isAiGenerateDisabled"
        :class="[
          'p-4',
          {
            'g-safe-area': _global.isMobileMode,
          },
        ]"
        @create-card="onCreateCard"
        @ai-generate="onAICardGenerate"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type Package,
  type CardResponse,
  type ChapterItem,
  CardTypeName,
} from '@/api/package-source'

import Empty from '@/components/Empty.vue'
import CardBrowserPad from '@/components/package/CardBrowserPad.vue'
import BottomButtons from './CardListBottomButtons.vue'
import PackageCoverBanner from '@/components/business/package/PackageCoverBanner.vue'

import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

import {
  getAllChapterTotalCardCount,
  getChapterChildren,
} from '@/utils/package'

import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import type { CardDraft } from '@/utils/card'

const props = defineProps<{
  chapterId: string
  package: Package
  cards: CardDraft[]
  isGenerating?: boolean
}>()

const emit = defineEmits<{
  chapterClick: [ChapterItem]
  cardClick: [CardResponse]
  createCard: []
  aiGenerate: []
  scroll: [Event]
  /** 一张卡片动画结束，传出 cardId */
  animationCompleted: [number]
  /** 所有动画都结束 */
  allAnimationCompleted: []
  showTitle: [boolean]
}>()

const dynamicScroller = ref()
const packageCoverBannerRef = ref()

const chapters = computed(() => {
  if (props.package)
    return getChapterChildren(props.package.chapters, props.chapterId)
  return []
})

const allChapterCountMap = computed(() => {
  if (props.package) return getAllChapterTotalCardCount(props.package)
  return {}
})

const isOwner = computed(() => props.package?.owned != null)

const isAiGenerateDisabled = computed(
  () =>
    props.package.cardType != null &&
    (props.package.cardType === CardTypeName.MCQ ||
      props.package.cardType === CardTypeName.KID_HANZI)
)

// package 是空的
const isPackageEmpty = computed(() => {
  return (
    Object.values(props.package.chapters).length === 0 &&
    props.cards.length === 0
  )
})

// 当前章节是空的 (没有卡片也没有子章节)
const isChapterEmpty = computed(() => {
  return chapters.value.length === 0 && props.cards.length === 0
})

// 显示章节title
const showTitle = computed(() => {
  if (chapters.value.length === 0) {
    return false
  }
  if (props.cards.length === 0 && !isOwner.value) {
    return false
  }
  return true
})

function scrollToIndex(index: number) {
  if (dynamicScroller.value) {
    dynamicScroller.value.scrollToItem(index)
  }
}

function scrollToBottom() {
  if (dynamicScroller.value) {
    dynamicScroller.value.scrollToBottom()
    dynamicScroller.value.scroll
  }
}

defineExpose({
  scrollToIndex,
  scrollToBottom,
  animateNewCards,
})

function onChapterClick(chapter: ChapterItem) {
  emit('chapterClick', chapter)
}

function onCardClick(card: CardResponse) {
  emit('cardClick', card)
}

function onCreateCard() {
  emit('createCard')
}

function onAICardGenerate() {
  emit('aiGenerate')
}

const inlineBottomButtons = useTemplateRef('inlineBottomButtons')
const showFixedBottomButtons = ref(false)

let bottomButtonsObserver: IntersectionObserver | null = null
let bannerObserver: IntersectionObserver | null = null

function initBottomButtonsObserver() {
  // 设置 IntersectionObserver 监听元素可见性变化
  bottomButtonsObserver = new IntersectionObserver(
    entries => {
      if (entries[0].isIntersecting) {
        showFixedBottomButtons.value = false
      } else {
        showFixedBottomButtons.value = true
      }
    },
    {
      threshold: [0, 1],
    }
  )

  bottomButtonsObserver.observe(inlineBottomButtons.value!.$el)
}

function initBannerObserver() {
  bannerObserver = new IntersectionObserver(
    entries => {
      console.log('bannerObserver', !entries[0].isIntersecting)
      emit('showTitle', !entries[0].isIntersecting)
    },
    {
      threshold: [0, 1],
    }
  )

  if (packageCoverBannerRef.value) {
    bannerObserver.observe(packageCoverBannerRef.value.$el)
  }
}

function clearObservers() {
  // 清理观察者
  if (bottomButtonsObserver) {
    bottomButtonsObserver.disconnect()
    bottomButtonsObserver = null
  }
  if (bannerObserver) {
    bannerObserver.disconnect()
    bannerObserver = null
  }
}

// 自动滚动标记，为true时AI制卡的新增项出现时会自动滚动到底部
// 默认为true，用户手动滚动过的话变为false，滚动到底部时再设为true
const autoScrollToBottom = ref<boolean>(true)
function onScroll(event: Event) {
  const { scrollTop, scrollHeight, clientHeight } = event.target as HTMLElement
  // 检测是否在底部附近（允许20px误差判断滚动到底部，解决浮点计算精度问题和跨浏览器差异）
  autoScrollToBottom.value = scrollHeight - scrollTop - clientHeight < 20
}

function onResize() {
  // 只有正在打字机效果的组件会触发这个事件，在这里尝试自动滚动
  if (autoScrollToBottom.value) {
    scrollToBottom()
  }
}

const bottomPadding = ref(0)

// 在 app 中键盘弹出时，需要检测一下当前的光标是否被键盘或者工具栏遮挡
// 如果被遮挡则需要滚动列表让光标可见
function handleSetBottomInsets(bottomInsets: number) {
  // ios 自动会处理键盘弹起的问题
  if (_store.appInfo?.platform === 'ios') return

  bottomPadding.value = bottomInsets

  // 键盘收起，不需要做处理
  if (bottomInsets === 0) {
    return
  }

  // 先让 padding 渲染出来（否则滚动时空间不够），然后再进行光标的滚动
  nextTick(() => {
    const selection = getSelection()
    const cardListRef = dynamicScroller.value?.$el as HTMLDivElement

    if (selection == null || cardListRef == null) return

    let focusElement: HTMLParagraphElement

    // 如果是 text 文本节点，则找出他的父元素，就是 p
    if (selection.focusNode?.nodeType === 3) {
      focusElement = selection.focusNode.parentElement as HTMLParagraphElement
    } else {
      focusElement = selection.focusNode as HTMLParagraphElement
    }

    const viewPortHeight = window.innerHeight - bottomInsets

    const rect = focusElement.getBoundingClientRect()

    // 如果光标在剩余视口的下面，则滚动这个差值就可以看到光标了
    if (rect.bottom > viewPortHeight) {
      cardListRef.scrollBy({
        top: rect.bottom - viewPortHeight,
      })
    }
  })
}

onMounted(() => {
  if (isOwner.value) {
    _bridge.editor.onSetBottomInsets(handleSetBottomInsets)
    initBottomButtonsObserver()
  }
  initBannerObserver()
})

onUnmounted(() => {
  if (isOwner.value) {
    _bridge.editor.offSetBottomInsets(handleSetBottomInsets)
    clearObservers()
  }
})

// 卡片生成打字机动画相关
const currentAnimatingCardId = ref<number | null>(null) // 当前正在动画的卡片ID
const queuedCardIds = ref<number[]>([]) // 等待动画的卡片ID队列

const showedCards = computed<CardDraft[]>(() =>
  props.cards.filter(
    item =>
      !queuedCardIds.value.includes(item.id) &&
      item.id !== currentAnimatingCardId.value
  )
)
const currentAnimatingCardDraft = computed<CardDraft | undefined>(() => {
  if (currentAnimatingCardId.value === null) {
    return undefined
  }

  return (
    props.cards.find(card => card.id === currentAnimatingCardId.value) ||
    undefined
  )
})

// 处理队列的方法
function processQueue() {
  if (queuedCardIds.value.length > 0) {
    const nextCardId = queuedCardIds.value.shift()!
    currentAnimatingCardId.value = nextCardId
    if (autoScrollToBottom.value) {
      scrollToBottom()
    }
  } else {
    currentAnimatingCardId.value = null
    emit('allAnimationCompleted')
  }
}

// 处理卡片动画完成事件
function onTypewriterComplete() {
  emit('animationCompleted', currentAnimatingCardId.value!)
  currentAnimatingCardId.value = null
  // 继续处理队列
  nextTick(() => {
    processQueue()
  })
}

// 公开方法：通知组件哪些卡片需要动画
function animateNewCards(cardIds: number[]) {
  if (cardIds.length > 0) {
    // 将这些卡片ID加入队列
    queuedCardIds.value.push(...cardIds)
    // 如果当前没有正在动画的卡片，开始处理队列
    if (currentAnimatingCardId.value === null) {
      processQueue()
    }
  }
}

// 监听章节变化
watch(
  () => props.chapterId,
  () => {
    // 清空队列，停止所有动画
    queuedCardIds.value = []
    currentAnimatingCardId.value = null
    emit('allAnimationCompleted')
  }
)
</script>

<style scoped>
.grid-chapter {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
}
.chapter {
  box-shadow: 0px 0px 4px 0px var(--slate-300) inset;
}
</style>
